import React from 'react';
import type { HeadingBlock } from '@/types/contentBlocks';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';

interface HeadingBlockProps {
  block: HeadingBlock;
  isDarkMode: boolean;
}

export const HeadingBlockRenderer: React.FC<HeadingBlockProps> = ({ block, isDarkMode }) => {
  const className = `font-bold ${
    block.level === 1
      ? 'text-3xl mt-5 mb-4'
      : block.level === 2
      ? 'text-2xl my-3'
      : block.level === 3
      ? 'text-xl mt-4 mb-2'
      : block.level === 4
      ? 'text-lg mt-3 mb-2'
      : 'text-base mt-2 mb-1'
  } ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`;

  // Use ReactMarkdown so inline formatting like **bold** or *italic* inside headings
  // is parsed correctly instead of showing literal asterisks/underscores.
  const Markdown = (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[[rehypeKatex, { strict: false, throwOnError: false, trust: true }]]}
      components={{
        // Prevent paragraphs wrapping inside headings
        p: ({ children }) => <>{children}</>,
      }}
    >
      {block.content}
    </ReactMarkdown>
  );

  switch (block.level) {
    case 1:
      return <h1 className={className}>{Markdown}</h1>;
    case 2:
      return <h2 className={className}>{Markdown}</h2>;
    case 3:
      return <h3 className={className}>{Markdown}</h3>;
    case 4:
      return <h4 className={className}>{Markdown}</h4>;
    case 5:
      return <h5 className={className}>{Markdown}</h5>;
    default:
      return <h6 className={className}>{Markdown}</h6>;
  }
};
